import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../redux/helpers/axios";

/**
 * @author
 * @function ElevenPayments
 *
 * Admin: 11.11 checkout sessions (one per checkout; the online advance
 * payment and the cash-on-delivery amount live here).
 **/

const API = {
  list: "/eleven/admin/payments",
  one: (id) => `/eleven/admin/payments/${id}`,
  confirm: (id) => `/eleven/admin/payments/${id}/confirm`,
  cancel: (id) => `/eleven/admin/payments/${id}/cancel`,
};

const STATUS = {
  UNPAID: { label: "Unpaid", cls: "bg-amber-100 text-amber-800" },
  PARTIALLY_PAID: {
    label: "Partly paid",
    cls: "bg-orange-100 text-orange-800",
  },
  PAID: { label: "Advance paid", cls: "bg-emerald-100 text-emerald-800" },
  REFUNDED: { label: "Refunded", cls: "bg-gray-200 text-gray-700" },
  PARTIALLY_REFUNDED: {
    label: "Partly refunded",
    cls: "bg-gray-200 text-gray-700",
  },
  CANCELLED: { label: "Cancelled", cls: "bg-gray-200 text-gray-700" },
};
const TABS = ["", "UNPAID", "PAID", "PARTIALLY_PAID", "CANCELLED", "REFUNDED"];

const ORDER_STATUS_LABEL = {
  PENDING: "Pending",
  APPROVED: "Approved",
  PROCESSING: "Processing",
  PURCHASED: "Purchased",
  IN_WAREHOUSE_CN: "China WH",
  IN_TRANSIT: "In transit",
  IN_WAREHOUSE_BD: "BD WH",
  DELIVERY_PROCESSING: "Delivery prep",
  HANDOVER_TO_COURIER: "With courier",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
  REFUNDED: "Refunded",
};

const ZONE = { INSIDE_DHAKA: "Inside Dhaka", OUTSIDE_DHAKA: "Outside Dhaka" };

const formatPrice = (n) =>
  new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const shortId = (s = "") =>
  s.length > 12 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;

/* ---------- small UI ---------- */

const Badge = ({ status }) => {
  const s = STATUS[status] || {
    label: status,
    cls: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}
    >
      {s.label}
    </span>
  );
};

const Copy = ({ value }) => {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(String(value));
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch {
          /* clipboard blocked */
        }
      }}
      className="text-[11px] font-medium text-indigo-600 hover:underline"
    >
      {done ? "Copied" : "Copy"}
    </button>
  );
};

const Stat = ({ label, value, hint, tone = "text-gray-800" }) => (
  <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
    {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

const Pagination = ({ p, onPage, disabled }) => {
  if (!p || p.totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 text-sm">
      <span className="text-gray-500">
        Page <b className="text-gray-800">{p.currentPage}</b> of {p.totalPages}{" "}
        · {p.total} total
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled || !p.hasPreviousPage}
          onClick={() => onPage(p.currentPage - 1)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          ← Prev
        </button>
        <button
          type="button"
          disabled={disabled || !p.hasNextPage}
          onClick={() => onPage(p.currentPage + 1)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className="fixed z-[70] top-4 right-4 max-w-sm">
      <div
        className={`rounded-lg px-4 py-3 shadow-lg text-sm text-white ${
          toast.type === "error" ? "bg-red-600" : "bg-gray-900"
        }`}
      >
        {toast.msg}
      </div>
    </div>
  );
};

const Drawer = ({ open, onClose, title, children }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-gray-50 shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

const inputCls =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500";

const Row = ({ label, value, strong }) => (
  <div className="flex justify-between gap-3 py-1.5 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className={strong ? "font-bold text-gray-900" : "text-gray-800"}>
      {value}
    </span>
  </div>
);

/* ---------- payment detail ---------- */

const PaymentDetail = ({ id, onChanged, notify }) => {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // "confirm" | "cancel"
  const [form, setForm] = useState({
    paidAmount: "",
    gatewayTrxId: "",
    note: "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(API.one(id));
      setP(data.payment);
      setForm((f) => ({
        ...f,
        paidAmount: String(Number(data.payment.requestedAmount)),
      }));
    } catch (err) {
      notify("error", err.response?.data?.msg || "Failed to load payment");
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (mode === "confirm") {
      if (!(Number(form.paidAmount) > 0))
        return setFormError("Enter the amount received");
      if (!form.gatewayTrxId.trim())
        return setFormError("Enter the transaction / reference ID");
      if (
        Number(form.paidAmount) < Number(p.requestedAmount) &&
        !window.confirm(
          `${formatPrice(form.paidAmount)} is less than the requested ${formatPrice(p.requestedAmount)}. Orders will stay PENDING. Continue?`,
        )
      )
        return;
    }
    if (
      mode === "cancel" &&
      !window.confirm("Cancel this checkout and all of its orders?")
    )
      return;

    setSaving(true);
    try {
      const { data } =
        mode === "confirm"
          ? await axiosInstance.post(API.confirm(id), {
              paidAmount: Number(form.paidAmount),
              gatewayTrxId: form.gatewayTrxId.trim(),
              note: form.note.trim() || undefined,
            })
          : await axiosInstance.post(API.cancel(id), {
              reason: form.reason.trim() || undefined,
            });
      notify("success", data.msg);
      setMode(null);
      await load();
      onChanged();
    } catch (err) {
      setFormError(err.response?.data?.msg || "Action failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !p) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!p) return null;

  const a = p.deliveryAddress;
  const unpaid = p.status === "UNPAID";

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Session ID</p>
            <p className="font-mono text-xs text-gray-800 break-all">
              {p.sessionId} <Copy value={p.sessionId} />
            </p>
          </div>
          <Badge status={p.status} />
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div
            className={`rounded-lg px-3 py-2 ${unpaid ? "bg-amber-50 border border-amber-200" : "bg-emerald-50 border border-emerald-200"}`}
          >
            <p className="text-[11px] font-bold uppercase text-gray-600">
              {unpaid ? "Advance due" : "Paid online"}
            </p>
            <p className="text-lg font-extrabold text-gray-900">
              {formatPrice(unpaid ? p.requestedAmount : p.paidAmount)}
            </p>
          </div>
          <div className="rounded-lg px-3 py-2 bg-indigo-50 border border-indigo-200">
            <p className="text-[11px] font-bold uppercase text-gray-600">
              Cash on delivery
            </p>
            <p className="text-lg font-extrabold text-gray-900">
              {formatPrice(p.codAmount)}
            </p>
          </div>
          <div className="rounded-lg px-3 py-2 bg-gray-50 border border-gray-200">
            <p className="text-[11px] font-bold uppercase text-gray-600">
              Order total
            </p>
            <p className="text-lg font-extrabold text-gray-900">
              {formatPrice(Number(p.subtotal) + Number(p.deliveryFee))}
            </p>
          </div>
        </div>
        <div className="mt-3 divide-y divide-gray-100">
          <Row label="Products subtotal" value={formatPrice(p.subtotal)} />
          <Row
            label={`Delivery fee${p.deliveryZone ? ` (${ZONE[p.deliveryZone] || p.deliveryZone})` : ""}`}
            value={
              Number(p.deliveryFee) > 0 ? formatPrice(p.deliveryFee) : "Free"
            }
          />
          <Row
            label="Requested advance"
            value={formatPrice(p.requestedAmount)}
          />
          <Row label="Paid amount" value={formatPrice(p.paidAmount)} strong />
          <Row
            label="Gateway transaction"
            value={
              p.gatewayTrxId ? (
                <span className="font-mono text-xs">
                  {p.gatewayTrxId} <Copy value={p.gatewayTrxId} />
                </span>
              ) : (
                "—"
              )
            }
          />
          <Row label="Paid at" value={formatDate(p.paidAt)} />
          <Row label="Created" value={formatDate(p.createdAt)} />
        </div>
      </div>

      {/* Customer + address */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Customer account</p>
          <p className="text-sm font-semibold text-gray-900">{p.user?.name}</p>
          <p className="text-xs text-gray-600 break-all">{p.user?.email}</p>
          {p.user?.phone && (
            <p className="text-xs text-gray-600">{p.user.phone}</p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Delivery address</p>
          {a ? (
            <>
              <p className="text-sm font-semibold text-gray-900">
                {a.name} · {a.phone} <Copy value={a.phone} />
              </p>
              <p className="text-xs text-gray-600">
                {a.street}, {a.city}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-400">—</p>
          )}
        </div>
      </div>

      {/* Orders */}
      <div className="bg-white rounded-xl border border-gray-200">
        <h3 className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-800">
          Orders ({p.orders.length})
        </h3>
        <ul className="divide-y divide-gray-100">
          {p.orders.map((o) => (
            <li key={o.id} className="px-4 py-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs font-semibold text-gray-800">
                  {o.orderNo}
                </span>
                <span className="text-xs font-semibold text-gray-600">
                  {ORDER_STATUS_LABEL[o.status] || o.status}
                </span>
              </div>
              <p className="text-gray-900 line-clamp-1 mt-0.5">
                {o.productTitle}
              </p>
              <p className="text-xs text-gray-500">
                {o.variationName ? `${o.variationName} · ` : ""}
                {o.totalQty} × {formatPrice(o.unitPrice)} ={" "}
                <b className="text-gray-800">{formatPrice(o.itemTotal)}</b>
                <span className="ml-2">
                  Paid{" "}
                  <b className="text-emerald-700">{formatPrice(o.totalPaid)}</b>{" "}
                  · Due{" "}
                  <b className="text-amber-700">{formatPrice(o.totalDue)}</b>
                </span>
              </p>
            </li>
          ))}
        </ul>
      </div>

      {/* Actions */}
      {unpaid && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          {!mode ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setMode("confirm")}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Confirm payment manually
              </button>
              <button
                type="button"
                onClick={() => setMode("cancel")}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-red-300 text-red-600 bg-white hover:bg-red-50"
              >
                Cancel checkout
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800">
                {mode === "confirm"
                  ? "Confirm advance payment"
                  : "Cancel checkout"}
              </h3>
              {mode === "confirm" ? (
                <>
                  <p className="text-xs text-gray-500">
                    Use this when the customer paid but the gateway callback
                    didn't arrive, or for a bank transfer. Orders move to
                    APPROVED when the full advance is recorded.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-700 mb-1">
                        Amount received (BDT)
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.paidAmount}
                        onChange={(e) =>
                          setForm({ ...form, paidAmount: e.target.value })
                        }
                        className={inputCls}
                      />
                    </label>
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-700 mb-1">
                        Transaction / reference ID
                      </span>
                      <input
                        value={form.gatewayTrxId}
                        onChange={(e) =>
                          setForm({ ...form, gatewayTrxId: e.target.value })
                        }
                        placeholder="e.g. bKash trxID or bank ref"
                        className={inputCls}
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-700 mb-1">
                      Internal note (optional)
                    </span>
                    <input
                      value={form.note}
                      onChange={(e) =>
                        setForm({ ...form, note: e.target.value })
                      }
                      placeholder="e.g. Verified in bKash merchant panel"
                      className={inputCls}
                    />
                  </label>
                </>
              ) : (
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">
                    Reason (shown in order history)
                  </span>
                  <input
                    value={form.reason}
                    onChange={(e) =>
                      setForm({ ...form, reason: e.target.value })
                    }
                    placeholder="e.g. Customer asked to cancel"
                    className={inputCls}
                  />
                </label>
              )}
              {formError && <p className="text-sm text-red-600">{formError}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-60 ${
                    mode === "confirm"
                      ? "bg-indigo-600 hover:bg-indigo-700"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {saving
                    ? "Saving…"
                    : mode === "confirm"
                      ? "Confirm payment"
                      : "Cancel checkout"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode(null);
                    setFormError("");
                  }}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                >
                  Back
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- page ---------- */

export const ElevenPayments = (props) => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ search: "", from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState(null);

  const notify = useCallback((type, msg) => setToast({ type, msg }), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get(API.list, {
        params: {
          page,
          status: status || undefined,
          search: filters.search || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
        },
      });
      setRows(data.payments || []);
      setStats(data.stats);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, [page, status, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = stats?.byStatus || {};
  const allCount = Object.values(counts).reduce((n, c) => n + c, 0);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] content-start">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Payments (Checkout Sessions)
          </h1>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat
          label="Awaiting payment"
          value={counts.UNPAID ?? 0}
          tone="text-amber-700"
        />
        <Stat
          label="Advance paid"
          value={(counts.PAID ?? 0) + (counts.PARTIALLY_PAID ?? 0)}
          tone="text-emerald-700"
        />
        <Stat
          label="Collected online"
          value={stats ? formatPrice(stats.collectedOnline) : "—"}
          hint="Paid + partly paid checkouts"
        />
        <Stat
          label="Cash to collect on delivery"
          value={stats ? formatPrice(stats.codExpected) : "—"}
          hint="For checkouts with advance paid"
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex gap-2 overflow-x-auto px-3 pt-3">
          {TABS.map((t) => (
            <button
              key={t || "all"}
              type="button"
              onClick={() => {
                setPage(1);
                setStatus(t);
              }}
              className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold border ${
                status === t
                  ? "bg-indigo-600 border-indigo-600 text-white"
                  : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
              }`}
            >
              {t ? STATUS[t].label : "All"}
              <span
                className={`ml-1.5 text-xs ${status === t ? "text-white/80" : "text-gray-400"}`}
              >
                {t ? (counts[t] ?? 0) : allCount}
              </span>
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setFilters((f) => ({ ...f, search: search.trim() }));
          }}
          className="flex flex-col lg:flex-row gap-2 px-3 py-3 border-b border-gray-100"
        >
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Session ID, transaction ID, order no., customer"
            className={`${inputCls} lg:w-96`}
          />
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={filters.from}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, from: e.target.value }));
              }}
              className={inputCls}
              aria-label="From date"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, to: e.target.value }));
              }}
              className={inputCls}
              aria-label="To date"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Search
          </button>
        </form>

        {error ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 text-sm font-semibold text-indigo-600 hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500">
                  <th className="px-3 py-2.5 font-medium">Session</th>
                  <th className="px-3 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 font-medium text-right">Orders</th>
                  <th className="px-3 py-2.5 font-medium text-right">
                    Advance
                  </th>
                  <th className="px-3 py-2.5 font-medium text-right">COD</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium">Created</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-gray-400"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-gray-400"
                    >
                      No payments found.
                    </td>
                  </tr>
                ) : (
                  rows.map((p) => (
                    <tr
                      key={p.id}
                      className={`border-t border-gray-100 hover:bg-gray-50 ${loading ? "opacity-60" : ""}`}
                    >
                      <td className="px-3 py-3">
                        <span
                          className="font-mono text-xs text-gray-800"
                          title={p.sessionId}
                        >
                          {shortId(p.sessionId)}
                        </span>{" "}
                        <Copy value={p.sessionId} />
                        {p.gatewayTrxId && (
                          <p className="text-[11px] text-gray-500 font-mono">
                            trx {p.gatewayTrxId}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-medium text-gray-900">
                          {p.deliveryAddress?.name || p.user?.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {p.deliveryAddress?.phone ||
                            p.user?.phone ||
                            p.user?.email}
                          {p.deliveryAddress?.city
                            ? ` · ${p.deliveryAddress.city}`
                            : ""}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-right text-gray-800">
                        {p.orderCount}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <p className="font-bold text-gray-900">
                          {formatPrice(
                            p.status === "UNPAID"
                              ? p.requestedAmount
                              : p.paidAmount,
                          )}
                        </p>
                        {p.status === "UNPAID" && (
                          <p className="text-[11px] text-amber-700">due</p>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-medium text-gray-800 whitespace-nowrap">
                        {formatPrice(p.codAmount)}
                      </td>
                      <td className="px-3 py-3">
                        <Badge status={p.status} />
                      </td>
                      <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(p.createdAt)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setOpenId(p.id)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <Pagination p={pagination} onPage={setPage} disabled={loading} />
      </div>

      <Drawer
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        title="Payment details"
      >
        {openId && (
          <PaymentDetail id={openId} onChanged={load} notify={notify} />
        )}
      </Drawer>
    </div>
  );
};

export default ElevenPayments;
